import { useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/outline';
import mmjCardService from '@/services/mmjCard.service';

const STATE_CONFIG = {
  pa: {
    heading: 'Enter Your PA Patient ID',
    submittedHeading: 'PA Patient ID Submitted',
    subtext:
      'Enter your Patient ID number from the Pennsylvania Department of Health registry. Your doctor needs this to complete your certification.',
    placeholder: 'Patient ID number',
    validate: () => true,
    errorMsg: '',
    helpText:
      "Don't have a Patient ID yet? Register on the PA Medical Marijuana Program Registry to receive one.",
    helpLink: 'https://padohmmp.custhelp.com/app/login',
    helpLinkText: 'PA MMJ Registry',
  },
  md: {
    heading: 'Enter Your MMCC Number',
    submittedHeading: 'MMCC Number Submitted',
    subtext:
      'Enter your 16-digit MMCC number from the OneStop portal. Your doctor needs this to activate your 12-month prescription.',
    placeholder: '16-digit MMCC number',
    validate: (val) => /^\d{16}$/.test(val),
    errorMsg: 'Please enter exactly 16 digits.',
    helpText:
      "Don't have your MMCC number yet? Register on the Maryland OneStop Portal to receive one.",
    helpLink:
      'https://onestop.md.gov/public_profiles/adult-patient-registration-601c0fd9f9d7557af267e1e1',
    helpLinkText: 'Maryland OneStop Portal',
  },
};

const StateIdInput = ({
  state,
  existingValue,
  onCardCreated,
  onSuccess,
  patientId,
  platformId,
}) => {
  const config = STATE_CONFIG[state];
  const [value, setValue] = useState(existingValue || '');
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(!!existingValue);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!value.trim()) return;

    if (!config.validate(value.trim())) {
      setError(config.errorMsg);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const { data } = await mmjCardService.createCard({
        state,
        patient_id: patientId,
        platform_id: platformId,
        mmj_issued: false,
        state_card_id: value.trim(),
      });
      if (onCardCreated) onCardCreated(data.uid);

      setSubmitted(true);
      setEditing(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError('Failed to save. Please try again.');
      console.log(err);
    } finally {
      setSaving(false);
    }
  };

  if (!config) return null;

  if (submitted && !editing) {
    return (
      <div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-3">
            <CheckCircleIcon className="h-8 w-8 text-green-500 flex-shrink-0" />
            <div className="font-bold text-lg text-green-800">
              {config.submittedHeading}
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 mb-3">
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
              Your ID Number
            </div>
            <div className="text-xl font-bold text-gray-900">{value}</div>
          </div>
          <p className="text-sm text-green-700 mb-3">
            Your doctor will review this before your appointment. No further
            action is needed on your part for this step.
          </p>
          <button
            type="button"
            className="text-sm text-gray-500 hover:underline"
            onClick={() => setEditing(true)}
          >
            Need to update this number?
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="font-bold text-lg mb-1">{config.heading}</h3>
      <p className="text-sm text-gray-600 mb-4">{config.subtext}</p>
      <div className="space-y-3">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={config.placeholder}
          className="w-full border border-gray-300 rounded-md px-4 py-3 text-base focus:outline-none focus:border-mmj-blue focus:ring-1 focus:ring-mmj-blue"
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving || !value.trim()}
          className={`w-full py-3 rounded-lg text-white font-medium text-base transition-colors ${
            !saving && value.trim()
              ? 'bg-mmj-blue hover:opacity-90'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          {saving ? 'Saving...' : 'Submit'}
        </button>
      </div>
      {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
      {config.helpText && (
        <div className="mt-4 p-4 bg-mmj-gray rounded-lg">
          <p className="text-sm text-gray-700">{config.helpText}</p>
          {config.helpLink && (
            <a
              href={config.helpLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-2 text-sm font-medium text-mmj-blue hover:underline"
            >
              {config.helpLinkText || config.helpLink}
            </a>
          )}
        </div>
      )}
      {editing && (
        <button
          type="button"
          className="mt-3 text-sm text-gray-500 hover:underline"
          onClick={() => {
            setEditing(false);
          }}
        >
          Cancel
        </button>
      )}
    </div>
  );
};

export default StateIdInput;
